import SegmentedToggle from '@components/ui/SegmentedToggle';
import { hasCasePack } from '@diagon-alley/common';
import { Group, NumberInput, type NumberInputProps, Stack, Text } from '@mantine/core';
import { semanticColors } from '@styles/design-system';
import { type FC, useState } from 'react';
import { formatCount } from '@utils/currency';

type Mode = 'units' | 'cases';

/**
 * A quantity box that can be typed in cases.
 *
 * THE VALUE IS UNITS, ALWAYS. Whatever the toggle says, `value` and `onChange` speak units,
 * because units are what every order, batch and dispatch stores. Cases mode is a way of
 * typing: 10 with a 72-a-case product becomes 720 before it leaves this component, and the
 * caller never learns which way it was typed.
 *
 * NO CASE SIZE, NO TOGGLE. A product without `units_per_case` gets a plain NumberInput with
 * the same props, so every brand that has never heard of cases sees exactly what it saw
 * before. The decision is per product, never per merchant: one order can carry a line in
 * cases beside a line in units.
 *
 * WHOLE CASES ONLY. Half a case of 45 is 22.5 units, which is not a thing. Somebody who needs
 * an odd amount switches to units; the hint underneath shows the case reading either way,
 * and turns amber when a unit quantity does not fill its last case - a nudge, never a block.
 */
export interface QuantityInputProps extends Omit<NumberInputProps, 'value' | 'onChange'> {
    /** Units. Blank is '' so a half-typed box does not read as zero. */
    value: number | '';
    onChange: (units: number | '') => void;
    /**
     * The product's case size. Absent or null means units only, no toggle.
     *
     * With a toggle, `className` lands on the whole control (box, toggle and hint together)
     * so a form's column class positions all of it, while `w` still sizes the number box
     * alone. Without one, every prop goes straight to the NumberInput as before.
     */
    unitsPerCase?: number | null;
}

/**
 * Whether the person last worked in cases, remembered across screens.
 *
 * Someone who orders in cases dispatches in cases; making them flip the toggle on every line
 * of every form is exactly the friction this exists to remove. Falls back to units whenever
 * storage is unavailable.
 */
const MODE_KEY = 'reparo.quantityMode';
const rememberedMode = (): Mode => {
    try {
        return localStorage.getItem(MODE_KEY) === 'cases' ? 'cases' : 'units';
    } catch {
        return 'units';
    }
};
const rememberMode = (mode: Mode): void => {
    try {
        localStorage.setItem(MODE_KEY, mode);
    } catch {
        // A remembered default is a convenience; losing it changes nothing.
    }
};

const MODES = [
    { label: 'Units', value: 'units' },
    { label: 'Cases', value: 'cases' },
];

const QuantityInput: FC<QuantityInputProps> = ({
    value,
    onChange,
    unitsPerCase,
    className,
    description,
    ...rest
}) => {
    const [mode, setMode] = useState<Mode>(rememberedMode);

    const emitUnits = (next: string | number) => onChange(typeof next === 'number' ? next : '');

    if (!hasCasePack(unitsPerCase)) {
        return (
            <NumberInput
                {...rest}
                description={description}
                className={className}
                value={value}
                onChange={emitUnits}
            />
        );
    }

    const units = value === '' ? null : value;
    const casesShown =
        units === null
            ? ''
            : Number.isInteger(units / unitsPerCase)
              ? units / unitsPerCase
              : Number((units / unitsPerCase).toFixed(2));

    let hint = `${unitsPerCase} per case`;
    let hintColor: string | undefined;
    if (units !== null && units > 0) {
        if (mode === 'cases') {
            hint = `= ${formatCount(units)} units`;
        } else {
            /*
             * A decimal, not "11 × 52 + 28". Somebody reading 600 units wants to know it is
             * eleven and a half cases, and the arithmetic form made them work that out. Two
             * places is enough to see the fraction; amber says the last case is not full.
             */
            const cases = units / unitsPerCase;
            const shown = formatCount(cases, { maxDecimals: 2 });
            hint = `= ${shown} ${cases === 1 ? 'case' : 'cases'}`;
            if (!Number.isInteger(cases)) hintColor = semanticColors.warning.dark;
        }
    }

    const height = rest.size === 'xs' ? 30 : 36;

    return (
        <Stack gap={2} className={className}>
            <Group gap={6} wrap="nowrap" align="flex-start">
                {mode === 'cases' ? (
                    <NumberInput
                        {...rest}
                        value={casesShown}
                        onChange={(next) =>
                            onChange(typeof next === 'number' ? Math.round(next * unitsPerCase) : '')
                        }
                        allowDecimal={false}
                    />
                ) : (
                    <NumberInput {...rest} value={value} onChange={emitUnits} />
                )}
                <SegmentedToggle
                    value={mode}
                    onChange={(next) => {
                        setMode(next as Mode);
                        rememberMode(next as Mode);
                    }}
                    options={MODES}
                    height={height}
                />
            </Group>
            {/*
              * ONE LINE UNDER THE BOX, not two. A caller's own caption ("Available: 150") is
              * folded in ahead of the case reading rather than rendered by the NumberInput as
              * a second line, so a row with a toggle stays the same height as a row without.
              */}
            <Text fz={11} c={hintColor ?? 'dimmed'} style={{ whiteSpace: 'nowrap' }}>
                {description ? (
                    <>
                        {description} · {hint}
                    </>
                ) : (
                    hint
                )}
            </Text>
        </Stack>
    );
};

export default QuantityInput;
